'use client';

import MagicJump from '@/components/MagicJump';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useTranslations } from '@/i18n/use-translations';
import { CartGroup, CartItem } from '../types/cart';
import { useCartSensorsTracking } from '../hooks/useCartSensorsTracking';

interface CartEmptyProps {
  className?: string;
  deviceType: EPlatform;
  canBuyList?: CartGroup[];
  canNotBuyList?: CartItem[];
  onLoginOnly?: () => void;
  onHandleSignInAndReg?: () => void;
  cartFunctionBtnClick?: (btnName: string) => void;
}

export function CartEmpty({
  className,
  deviceType,
  canBuyList = [],
  canNotBuyList = [],
  onLoginOnly,
  onHandleSignInAndReg,
  cartFunctionBtnClick,
}: CartEmptyProps) {
  const t = useTranslations('cart.empty');
  const token = useStore((state) => state.token);
  const publicResourceData = useStore((state) => state.publicResourceData);
  const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
  const { trackCartFunctionBtnClick } = useCartSensorsTracking();

  // 直接从 publicResourceData 获取配置，避免单独的 state
  const cartEmptyNavigationConfig = Array.isArray(
    publicResourceData?.jsonvalues?.cart_empty_navigation_config,
  )
    ? (publicResourceData.jsonvalues.cart_empty_navigation_config as any[])
    : [];

  const goToLogin = () => {
    trackCartFunctionBtnClick('Sign in');
    setShowLoginRegisterDialog({
      show: true,
      eventType: 'shopping_cart_null',
      situation: '购物车登录',
    });
    if (canBuyList.length === 0 && canNotBuyList.length !== 0) {
      onLoginOnly?.();
    } else if (canBuyList.length === 0 && canNotBuyList.length === 0) {
      onHandleSignInAndReg?.();
    }
  };

  // PC端渲染
  if (deviceType === EPlatform.WEB) {
    return (
      <div
        className={`flex flex-col items-center justify-center pt-[104px] pb-[104px] bg-[#f5f6f7] ${className}`}
      >
        <div className="text-[40px] leading-[48px] font-bb text-center">{t('title')}</div>
        {token ? (
          <div className="text-center mt-[16px]">
            <p className="text-[14px] leading-[17px]">{t('loggedInMessage')}</p>
          </div>
        ) : (
          <div className="text-center mt-[16px]">
            <p className="text-[14px] leading-[17px]">{t('alreadyMember')}</p>
            <div className="text-[14px] leading-[17px]">
              <span
                className="text-primary-purple cursor-pointer underline"
                onClick={() => {
                  goToLogin();
                }}
              >
                {t('signIn')}
              </span>{' '}
              {t('toViewSaved')}
            </div>
          </div>
        )}
      </div>
    );
  }

  // WAP端渲染
  return (
    <div className="flex flex-col items-center justify-center pt-[48px] pb-[40px] bg-[#f5f6f7]">
      <div className="text-[20px] font-bb text-center">{t('title')}</div>
      {token ? (
        <div className="text-center mt-[12px]">
          <p className="text-[11px] leading-[17px]">{t('loggedInMessage')}</p>
        </div>
      ) : (
        <div className="text-center mt-[12px]">
          <p className="text-[11px] leading-[17px]">{t('alreadyMember')}</p>
          <div className="text-[11px] leading-[17px]">
            <span className="underline text-primary-purple cursor-pointer" onClick={goToLogin}>
              {t('signIn')}
            </span>{' '}
            {t('toViewSaved')}
          </div>
        </div>
      )}
      {cartEmptyNavigationConfig.length > 0 && (
        <div className="mt-[24px]">
          {cartEmptyNavigationConfig.map((item, index) => (
            <div
              key={item.btn_name}
              className={`w-[292px] h-[36px] rounded-[22px] leading-[36px] text-center ${
                index === cartEmptyNavigationConfig.length - 1 ? 'mt-[12px]' : ''
              }`}
              style={{
                backgroundColor: item.background_color,
                color: item.text_color,
              }}
            >
              <MagicJump
                item={item.jump_info}
                onBeforeJump={() => cartFunctionBtnClick?.(item.btn_name)}
                className="w-full h-full block"
              >
                <span className="text-[14px] font-bb">{item.btn_name}</span>
              </MagicJump>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
